import { useEffect, useRef } from 'react'

// Simple GSAP-like animation hook using CSS transitions and Intersection Observer
export function useScrollReveal(options = {}) {
  const ref = useRef<HTMLElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    // Set initial state
    element.style.opacity = '0'
    element.style.transform = 'translateY(30px)'
    element.style.transition = 'opacity 0.8s ease-out, transform 0.8s ease-out'

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            element.style.opacity = '1'
            element.style.transform = 'translateY(0)'
          }
        })
      },
      { threshold: 0.1, ...options },
    )

    observer.observe(element)

    return () => observer.disconnect()
  }, [])

  return ref
}

export function useStaggerReveal(delay = 100) {
  const ref = useRef<HTMLElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const children = Array.from(element.children) as HTMLElement[]

    children.forEach((child, index) => {
      child.style.opacity = '0'
      child.style.transform = 'translateY(20px)'
      child.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out'
      child.style.transitionDelay = `${index * delay}ms`
    })

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            children.forEach((child) => {
              child.style.opacity = '1'
              child.style.transform = 'translateY(0)'
            })
          }
        })
      },
      { threshold: 0.1 },
    )

    observer.observe(element)

    return () => observer.disconnect()
  }, [delay])

  return ref
}

export function useParallax(speed = 0.5) {
  const ref = useRef<HTMLElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const handleScroll = () => {
      const scrolled = window.scrollY
      const elementTop = element.offsetTop
      const offset = (scrolled - elementTop) * speed
      element.style.transform = `translateY(${offset}px)`
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [speed])

  return ref
}
